/**
 * SOURCE OF TRUTH: which localStorage key maps to which cloud table + serializer.
 *
 * Every piece of synced app state is listed here exactly once. Later chunks
 * (push/pull/sync) drive entirely off this map — they should never hard-code a
 * key or table name. Caches, the cryptolens_* legacy keys, and the migration
 * flag are intentionally absent (they stay client-side, per the schema notes).
 *
 * Each descriptor's `shape` selects how to sync it:
 *   - 'multi'      append-style table; serializer = CLOUD_TABLES[table].toRow/fromRow
 *   - 'snapshots'  portfolio_snapshots (composite user_id+date PK)
 *   - 'draft'      one field of the single strategy_drafts row
 *   - 'preference' one field of the single user_preferences JSONB blob; the raw
 *                  localStorage string <-> typed value codec lives in PREFERENCE_CODECS
 */
import { transactionsTable } from './transactions';
import { snapshotsTable } from './snapshots';
import { connectedWalletsTable } from './connectedWallets';
import { buyPlansTable } from './buyPlans';
import { sellPlansTable } from './sellPlans';
import { taxEstimatesTable } from './taxEstimates';
import { strategyDraftsTable, type StrategyDraftsValue } from './strategyDrafts';
import {
  userPreferencesTable,
  PREFERENCE_CODECS,
  type AppPreferences,
} from './userPreferences';

export type CloudTableName =
  | 'transactions'
  | 'portfolio_snapshots'
  | 'connected_wallets'
  | 'saved_buy_plans'
  | 'saved_sell_plans'
  | 'tax_estimates'
  | 'strategy_drafts'
  | 'user_preferences';

/** Registry of every table API (each carries its own toRow/fromRow serializer). */
export const CLOUD_TABLES = {
  transactions: transactionsTable,
  portfolio_snapshots: snapshotsTable,
  connected_wallets: connectedWalletsTable,
  saved_buy_plans: buyPlansTable,
  saved_sell_plans: sellPlansTable,
  tax_estimates: taxEstimatesTable,
  strategy_drafts: strategyDraftsTable,
  user_preferences: userPreferencesTable,
} as const;

// ── Descriptor variants ──────────────────────────────────────────────────────

/** Append-style table: a localStorage array maps row-for-row to its table. */
export interface MultiRowDescriptor {
  shape: 'multi';
  table: 'transactions' | 'connected_wallets' | 'saved_buy_plans' | 'saved_sell_plans' | 'tax_estimates';
}

/** portfolio_snapshots: array of {date,value}, composite (user_id,date) PK. */
export interface SnapshotsDescriptor {
  shape: 'snapshots';
  table: 'portfolio_snapshots';
}

/** One of the three drafts inside the single strategy_drafts row. */
export interface DraftDescriptor {
  shape: 'draft';
  table: 'strategy_drafts';
  field: keyof StrategyDraftsValue;
}

/** One key inside the single user_preferences JSONB blob. */
export interface PreferenceDescriptor {
  shape: 'preference';
  table: 'user_preferences';
  prefKey: keyof AppPreferences;
}

export type SyncDescriptor =
  | MultiRowDescriptor
  | SnapshotsDescriptor
  | DraftDescriptor
  | PreferenceDescriptor;

// ── The map ──────────────────────────────────────────────────────────────────

export const SYNC_KEYS: Record<string, SyncDescriptor> = {
  // Append-style tables.
  mycryptoplanner_transactions: { shape: 'multi', table: 'transactions' },
  mycryptoplanner_connected_wallets: { shape: 'multi', table: 'connected_wallets' },
  savedBuyPlans: { shape: 'multi', table: 'saved_buy_plans' },
  savedSellPlans: { shape: 'multi', table: 'saved_sell_plans' },
  taxPlanEstimates: { shape: 'multi', table: 'tax_estimates' },

  // Daily snapshots (composite PK).
  mycryptoplanner_snapshots: { shape: 'snapshots', table: 'portfolio_snapshots' },

  // Open planner drafts -> the single strategy_drafts row.
  mycryptoplanner_buy_plan: { shape: 'draft', table: 'strategy_drafts', field: 'buyPlan' },
  mycryptoplanner_sell_plan: { shape: 'draft', table: 'strategy_drafts', field: 'sellPlan' },
  mycryptoplanner_tax_harvest: { shape: 'draft', table: 'strategy_drafts', field: 'taxHarvest' },

  // Settings / onboarding -> the single user_preferences JSONB blob.
  mycryptoplanner_theme: { shape: 'preference', table: 'user_preferences', prefKey: 'theme' },
  mycryptoplanner_currency: { shape: 'preference', table: 'user_preferences', prefKey: 'currency' },
  mycryptoplanner_chart_view: { shape: 'preference', table: 'user_preferences', prefKey: 'chartView' },
  mycryptoplanner_holdings_wallet_filter: { shape: 'preference', table: 'user_preferences', prefKey: 'holdingsWalletFilter' },
  cashOutGoal: { shape: 'preference', table: 'user_preferences', prefKey: 'cashOutGoal' },
  goalShowProjection: { shape: 'preference', table: 'user_preferences', prefKey: 'goalShowProjection' },
  cgtBracketIdx: { shape: 'preference', table: 'user_preferences', prefKey: 'cgtBracketIdx' },
  sampleDataDismissed: { shape: 'preference', table: 'user_preferences', prefKey: 'sampleDataDismissed' },
};

/**
 * Convenience: the localStorage keys that all feed the single user_preferences
 * blob, each paired with its raw-string <-> typed-value codec. (Same data as the
 * 'preference' entries in SYNC_KEYS, pre-joined to their codec for sync code.)
 */
export const PREFERENCE_SYNC_KEYS = PREFERENCE_CODECS;
